/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/* Beautiful Rivers variant UX fix @4
 * Keeps dependent Shopify options valid, including the exclusive Glow style/format,
 * keeps the selected variant media visible, and orders print formats by family/size.
 */
(() => {
  "use strict";

  if (window.__beautifulRiversVariantUiV4) return;
  window.__beautifulRiversVariantUiV4 = true;

  const productCache = new Map();
  const productData = new Map();
  const familyOrder = [
    /^(Fine art emoldurado|Framed fine art print|Impresión fine art enmarcada|Tirage fine art encadré|Gerahmter Fine-Art-Druck)\b/i,
    /^(Acrílico|Acrylic|Acrylique|Acrylglas)\b/i,
    /^(Canvas|Lienzo|Toile|Leinwand)\b/i,
    /^(Impressão fine art|Fine art print|Impresión fine art|Tirage fine art|Fine-Art-Druck)\b/i,
    /^(Póster fosforescente|Glow-in-the-dark poster|Póster que brilla en la oscuridad|Affiche phosphorescente|Nachleuchtendes Poster)\b/i,
  ];
  const finishOrder = [
    "Sem margem",
    "Margem branca",
    "Black · Sem margem",
    "White · Sem margem",
    "Natural · Sem margem",
    "Preto · Sem margem",
    "Branco · Sem margem",
    "Black · No border",
    "White · No border",
    "Natural · No border",
    "Negro · Sin margen",
    "Blanco · Sin margen",
    "Natural · Sin margen",
    "Noir · Sans marge",
    "Blanc · Sans marge",
    "Naturel · Sans marge",
    "Schwarz · Randlos",
    "Weiß · Randlos",
    "Natur · Randlos",
  ];

  function productHandle(root) {
    return root?.dataset.productHandle || root?.closest("product-info")?.dataset.url?.split("/").filter(Boolean).pop() || "";
  }

  function loadProduct(root) {
    const handle = productHandle(root);
    if (!handle) return Promise.resolve(null);
    if (!productCache.has(handle)) {
      const routeRoot = window.Shopify?.routes?.root || "/";
      productCache.set(handle, fetch(`${routeRoot}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: "application/json" },
      }).then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      }).then((product) => {
        productData.set(handle, product);
        return product;
      }).catch((error) => {
        console.error("[Beautiful Rivers] Não foi possível carregar as variantes.", error);
        productCache.delete(handle);
        return null;
      }));
    }
    return productCache.get(handle);
  }

  function formatSortKey(value, originalIndex) {
    const family = familyOrder.findIndex((pattern) => pattern.test(value));
    const dimensions = [...value.matchAll(/(\d+)\s*[×x]\s*(\d+)/gi)].at(-1);
    const width = dimensions ? Number(dimensions[1]) : Number.MAX_SAFE_INTEGER;
    const height = dimensions ? Number(dimensions[2]) : Number.MAX_SAFE_INTEGER;
    return [family < 0 ? familyOrder.length : family, width * height, width, height, originalIndex];
  }

  function compareKeys(left, right) {
    for (let index = 0; index < left.length; index += 1) {
      if (left[index] !== right[index]) return left[index] - right[index];
    }
    return 0;
  }

  function orderFormats(root) {
    const formatInputs = optionInputs(root, 1);
    if (formatInputs.length < 2) return;
    const fieldset = formatInputs[0].closest("fieldset");
    if (!fieldset) return;
    const entries = formatInputs.map((input, originalIndex) => ({
      input,
      label: fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`),
      key: formatSortKey(input.value, originalIndex),
    }));
    entries.sort((left, right) => compareKeys(left.key, right.key));
    for (const { input, label } of entries) {
      fieldset.append(input);
      if (label) fieldset.append(label);
    }
  }

  function fieldsets(root) {
    return [...root.querySelectorAll("fieldset")].filter((fieldset) => fieldset.querySelector("input[type=radio]"));
  }

  function optionInputs(root, index) {
    const fieldset = fieldsets(root)[index];
    return fieldset ? [...fieldset.querySelectorAll("input[type=radio]")] : [];
  }

  function optionIndex(root, input) {
    return fieldsets(root).indexOf(input.closest("fieldset"));
  }

  function selectedValue(root, index) {
    return optionInputs(root, index).find((input) => input.checked)?.value || "";
  }

  function optionValue(variant, index) {
    return variant?.[`option${index + 1}`] ?? variant?.options?.[index] ?? "";
  }

  function dimensions(value) {
    const match = [...String(value).matchAll(/(\d+)\s*[×x]\s*(\d+)/gi)].at(-1);
    return match ? [Number(match[1]), Number(match[2])] : null;
  }

  function dimensionDistance(left, right) {
    const a = dimensions(left);
    const b = dimensions(right);
    if (!a || !b) return Number.MAX_SAFE_INTEGER;
    return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]);
  }

  function finishRank(value) {
    const rank = finishOrder.indexOf(value);
    return rank < 0 ? finishOrder.length : rank;
  }

  function chooseCompatibleVariant(root, product, changedIndex, changedValue) {
    if (!product?.variants?.length) return;
    const current = [selectedValue(root, 0), selectedValue(root, 1), selectedValue(root, 2)];
    current[changedIndex] = changedValue;
    const candidates = product.variants.filter((variant) =>
      variant.available && optionValue(variant, changedIndex) === changedValue,
    );
    if (!candidates.length) return;

    const styleOrder = optionInputs(root, 0).map((input) => input.value);
    candidates.sort((left, right) => {
      const leftStyle = optionValue(left, 0);
      const rightStyle = optionValue(right, 0);
      const leftFormat = optionValue(left, 1);
      const rightFormat = optionValue(right, 1);
      const leftFinish = optionValue(left, 2);
      const rightFinish = optionValue(right, 2);
      const leftKey = changedIndex === 0
        ? [
            leftFormat === current[1] ? 0 : 1,
            dimensionDistance(leftFormat, current[1]),
            ...formatSortKey(leftFormat, 0),
            leftFinish === current[2] ? 0 : 1,
            finishRank(leftFinish),
          ]
        : [
            leftStyle === current[0] ? 0 : 1,
            styleOrder.indexOf(leftStyle),
            leftFinish === current[2] ? 0 : 1,
            finishRank(leftFinish),
          ];
      const rightKey = changedIndex === 0
        ? [
            rightFormat === current[1] ? 0 : 1,
            dimensionDistance(rightFormat, current[1]),
            ...formatSortKey(rightFormat, 0),
            rightFinish === current[2] ? 0 : 1,
            finishRank(rightFinish),
          ]
        : [
            rightStyle === current[0] ? 0 : 1,
            styleOrder.indexOf(rightStyle),
            rightFinish === current[2] ? 0 : 1,
            finishRank(rightFinish),
          ];
      return compareKeys(leftKey, rightKey);
    });

    const chosen = candidates[0];
    for (let index = 0; index < 3; index += 1) {
      // The browser must toggle the option that the customer clicked itself.
      // Pre-checking it suppresses the native change event Dawn uses to update
      // the variant URL and product media.
      if (index === changedIndex) continue;
      const desired = optionValue(chosen, index);
      const input = optionInputs(root, index).find((item) => item.value === desired);
      if (input) input.checked = true;
    }
  }

  function revealActiveVariantMedia(gallery) {
    const active = gallery?.querySelector(".product__media-item.is-active");
    if (!active) return;
    active.removeAttribute("data-br-style-hidden");
    active.removeAttribute("aria-hidden");
  }

  function installActiveMediaGuard(root) {
    const gallery = root.closest(".product")?.querySelector("media-gallery");
    if (!gallery || gallery.dataset.brActiveMediaGuard === "true") return;
    gallery.dataset.brActiveMediaGuard = "true";
    revealActiveVariantMedia(gallery);
    new MutationObserver(() => revealActiveVariantMedia(gallery)).observe(gallery, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "data-br-style-hidden", "aria-hidden"],
    });
  }

  function initializeRoot(root) {
    if (!(root instanceof Element) || root.localName !== "variant-selects") return;
    orderFormats(root);
    installActiveMediaGuard(root);
    void loadProduct(root);
  }

  function boot() {
    document.querySelectorAll("variant-selects").forEach(initializeRoot);

    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.localName === "variant-selects") initializeRoot(node);
          node.querySelectorAll?.("variant-selects").forEach(initializeRoot);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener("click", (event) => {
      const label = event.target.closest?.("variant-selects label[for]");
      if (!label) return;
      const root = label.closest("variant-selects");
      const input = root?.querySelector(`#${CSS.escape(label.htmlFor)}`);
      if (!input || ![0, 1].includes(optionIndex(root, input))) return;
      const handle = productHandle(root);
      const loaded = productData.get(handle);
      if (loaded) {
        chooseCompatibleVariant(root, loaded, optionIndex(root, input), input.value);
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      void loadProduct(root).then((product) => {
        if (product && input.isConnected) input.click();
      });
    }, true);

    document.addEventListener("change", (event) => {
      const input = event.target.closest?.('variant-selects input[type="radio"]');
      const root = input?.closest("variant-selects");
      if (!input || !root || ![0, 1].includes(optionIndex(root, input))) return;
      const product = productData.get(productHandle(root));
      if (product) chooseCompatibleVariant(root, product, optionIndex(root, input), input.value);
    }, true);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();
})();
